import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Sensor } from '../types/Sensor';
import { getSensors } from '../services/sensorData';

interface SensorContextType {
  sensors: Sensor[];
  loading: boolean;
  error: string | null;
  refreshSensors: () => Promise<void>;
  getSensorsByType: (sensorType: string) => Sensor[];
  getSensorById: (id: string) => Sensor | undefined;
}

const SensorContext = createContext<SensorContextType | undefined>(undefined);

interface SensorProviderProps {
  children: ReactNode;
}

export const SensorProvider: React.FC<SensorProviderProps> = ({ children }) => {
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  const fetchSensors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const storeId = localStorage.getItem('storeId');
      
      if (!storeId) {
        // console.log('Store ID not found in SensorContext, retry count:', retryCount);
        
        // Retry up to 3 times with increasing delays
        if (retryCount < 3) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            fetchSensors();
          }, 500 * (retryCount + 1)); // 500ms, 1s, 1.5s delays
          return;
        }
        
        setError('Store ID not found in localStorage');
        setLoading(false);
        return;
      }
      
      // Reset retry count when storeId is found
      setRetryCount(0);
      
      console.log('Fetching sensors for storeId:', storeId); // Debug log
      
      const response = await getSensors(storeId);
      setSensors(response);
      
    } catch (err) {
      setError('Failed to fetch sensors');
      console.error('Error fetching sensors:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSensors = async () => {
    setRetryCount(0); // Reset retry count when manually refreshing
    await fetchSensors();
  };

  const getSensorsByType = (sensorType: string): Sensor[] => {
    return sensors.filter(sensor => sensor.sensor_type === sensorType);
  };

  const getSensorById = (id: string): Sensor | undefined => {
    return sensors.find(sensor => sensor.id === id);
  };

  useEffect(() => {
    fetchSensors();
  }, []);

  const value: SensorContextType = {
    sensors,
    loading,
    error,
    refreshSensors,
    getSensorsByType,
    getSensorById,
  };

  return (
    <SensorContext.Provider value={value}>
      {children}
    </SensorContext.Provider>
  );
};

export const useSensors = (): SensorContextType => {
  const context = useContext(SensorContext);
  if (context === undefined) {
    throw new Error('useSensors must be used within a SensorProvider');
  }
  return context;
}; 